import type { ReturnVisitReading } from '@/state/experiments/segment-rows';
import type { StackedField } from '../../../../ConsolePanel';
import { cn } from 'helpers/cn';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../../../ConsolePanel';

type ReturnVisitsTableProps = {
  readings: readonly ReturnVisitReading[];
  windowDays: number;
  visitGapMinutes: number;
};

const TEMPLATE = 'minmax(160px,1fr) repeat(3, minmax(120px,auto))';

/** Return visits per variant: an outcome of its own, which no other table on the page measures. */
export const ReturnVisitsTable = ({ readings, windowDays, visitGapMinutes }: ReturnVisitsTableProps) => {
  const rows = readings.map((reading) => ({
    reading,
    fields: [
      { key: 'matured', label: 'Visitors', value: reading.matured.toLocaleString() },
      { key: 'per', label: 'Return visits per visitor', value: reading.perVisitor.toFixed(2) },
      { key: 'share', label: 'Came back at least once', value: `${(reading.share * 100).toFixed(1)}%` },
    ] satisfies StackedField[],
  }));

  const noneMatured = readings.every((r) => r.matured === 0);

  return (
    <div className="flex flex-col gap-2">
      <p className="supporting">
        Visits after the one they enrolled in, within {windowDays} days of enrolling. Only visitors who enrolled at
        least {windowDays} days ago count, so that variants which got their visitors later are not penalised for it. A
        new tab within {visitGapMinutes} minutes of the last one is the same visit, not a return.
      </p>

      {noneMatured ? (
        <p className="supporting">No visitor with an id has passed the {windowDays}-day window yet.</p>
      ) : (
        <div className="border-line bg-panel2 rounded-card border">
          <div className="flex flex-col gap-2 p-3 md:hidden">
            {rows.map(({ reading, fields }) => (
              <StackedRow
                key={reading.variantId}
                className={cn(reading.tooSmall && 'opacity-60')}
                heading={
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="text-foreground truncate text-body">{reading.name}</span>
                    {reading.isControl && <span className="micro-label shrink-0">control</span>}
                  </div>
                }
                fields={fields}
              />
            ))}
          </div>

          <GridTable label="Return visits" minWidth={600} template={TEMPLATE}>
            <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
              <div role="columnheader" className="micro-label">
                Variant
              </div>
              {rows[0]?.fields.map((field) => (
                <div key={field.key} role="columnheader" className="micro-label text-right">
                  {field.label}
                </div>
              ))}
            </div>
            <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
              {rows.map(({ reading, fields }) => (
                <div
                  key={reading.variantId}
                  role="row"
                  className={cn(
                    GRID_TABLE_ROW,
                    'border-line items-center border-b py-2 last:border-b-0',
                    reading.tooSmall && 'opacity-60',
                  )}
                >
                  <div role="cell" className="num text-foreground truncate font-sans text-body">
                    {reading.name}
                    {reading.isControl && <span className="micro-label ml-2">control</span>}
                  </div>
                  {fields.map((field) => (
                    <div key={field.key} role="cell" className="num text-foreground text-right">
                      {field.value}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </GridTable>
        </div>
      )}
    </div>
  );
};
